import { Dialog, DialogType, FontWeights, mergeStyleSets, MessageBar, MessageBarType, Spinner, SpinnerSize } from '@fluentui/react';
import React from 'react';
import { useApplicationContext } from '../hooks/useApplicationContext';
import { useAuthenticationContext } from '../hooks/useAuthenticationContext';
import { IGuild } from '../models/IGuild';

interface IGuildPickerProps
{
    onSelect(guild: IGuild | undefined): void;
}

const lastGuildStorageKey = "Uhrevind_LastGuildId";

const avatarColors = ["#0078d4", "#107c10", "#8764b8", "#ca5010", "#038387", "#c239b3", "#986f0b", "#4f6bed"];

const styles = mergeStyleSets({
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 8
    },
    sectionLabel: {
        fontSize: 12,
        fontWeight: FontWeights.semibold,
        color: '#605e5c',
        margin: '16px 0 6px 0'
    },
    card: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '10px 12px',
        border: '1px solid #edebe9',
        borderRadius: 6,
        background: '#ffffff',
        cursor: 'pointer',
        textAlign: 'left',
        font: 'inherit',
        transition: 'border-color 0.1s, box-shadow 0.1s, background 0.1s',
        selectors: {
            ':hover:not(:disabled)': { borderColor: '#0078d4', boxShadow: '0 1.6px 3.6px rgba(0,0,0,0.13)' },
            ':focus-visible': { outline: '2px solid #0078d4', outlineOffset: 1 },
            ':disabled': { cursor: 'default', opacity: 0.5 }
        }
    },
    featuredCard: {
        padding: '14px 16px',
        borderColor: '#c7e0f4',
        background: '#f3f9fd'
    },
    avatar: {
        flexShrink: 0,
        boxSizing: 'border-box',
        minWidth: 36,
        height: 36,
        padding: '0 8px',
        borderRadius: 18,
        whiteSpace: 'nowrap',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff',
        fontSize: 13,
        fontWeight: FontWeights.semibold
    },
    featuredAvatar: {
        minWidth: 44,
        height: 44,
        borderRadius: 22,
        fontSize: 15
    },
    text: {
        flexGrow: 1,
        minWidth: 0
    },
    name: {
        fontSize: 14,
        color: '#323130',
        overflowWrap: 'anywhere'
    },
    hint: {
        fontSize: 12,
        color: '#605e5c'
    }
});

// Brugere logget ind før ShortName kom med i login-svaret har det ikke i sessionStorage
const getAvatarText = (guild: IGuild): string => guild.shortName || guild.name.substring(0, 2).toUpperCase();

// Samme laug får altid samme farve
const getColor = (id: string): string => {
    let hash = 0;
    for(let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
    return avatarColors[Math.abs(hash) % avatarColors.length];
}

const readLastGuildId = (): string | null => {
    try { return localStorage.getItem(lastGuildStorageKey); } catch { return null; }
}

const writeLastGuildId = (id: string) => {
    try { localStorage.setItem(lastGuildStorageKey, id); } catch { }
}

export const GuildPicker: React.FunctionComponent<IGuildPickerProps> = (props: React.PropsWithChildren<IGuildPickerProps>) => {
    const { Services } = useApplicationContext();
    const { user } = useAuthenticationContext();
    const [pendingId, setPendingId] = React.useState<string>();
    const [error, setError] = React.useState<string>();

    if(user == null) return <></>;

    const lastGuildId = readLastGuildId();
    const lastGuild = user.guilds.length > 1 ? user.guilds.filter(x => x.id == lastGuildId)[0] : undefined;
    const otherGuilds = user.guilds.filter(x => x !== lastGuild);

    const select = (guild: IGuild) => {
        if(pendingId != undefined) return;
        setError(undefined);
        setPendingId(guild.id);
        const fail = () => { setPendingId(undefined); setError(`Kunne ikke åbne ${guild.name}. Prøv igen.`); };
        Services?.Master.SetGuild(guild)
            .then((ok) => {
                if(!ok) return fail();
                writeLastGuildId(guild.id);
                props.onSelect(guild);
            })
            .catch(fail);
    }

    const renderCard = (guild: IGuild, featured: boolean) => (
        <button key={guild.id}
            type="button"
            className={featured ? `${styles.card} ${styles.featuredCard}` : styles.card}
            disabled={pendingId != undefined && pendingId != guild.id}
            onClick={() => select(guild)}
            title={guild.name}
        >
            <div className={featured ? `${styles.avatar} ${styles.featuredAvatar}` : styles.avatar} style={{ backgroundColor: getColor(guild.id) }}>
                {getAvatarText(guild)}
            </div>
            <div className={styles.text}>
                {featured && <div className={styles.hint}>Fortsæt med</div>}
                <div className={styles.name} style={featured ? { fontWeight: FontWeights.semibold } : undefined}>{guild.name}</div>
            </div>
            {pendingId == guild.id && <Spinner size={SpinnerSize.small} />}
        </button>
    );

    return (<Dialog  hidden={false}
        isBlocking={true}
        dialogContentProps={{
            type: DialogType.largeHeader,
            title: `Vælg vindmøllelaug`
        }}
        modalProps={{
            isBlocking: true,
        }}
        minWidth={'min(680px, 92vw)'}
        maxWidth={'92vw'}
    >
        {error && <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: 12 } }}>{error}</MessageBar>}
        {lastGuild && <>
            {renderCard(lastGuild, true)}
            <div className={styles.sectionLabel}>Andre laug</div>
        </>}
        <div className={styles.grid}>
            {otherGuilds.map(x => renderCard(x, false))}
        </div>
    </Dialog>);
}

export default GuildPicker;
